import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { LoggerMeasurementChartDataApiService } from '@app/api/services/logger-measurement-chart-data-api.service';
import {
  IFpLoggerNoiseSpectrumState,
  initialFpLoggerNoiseSpectrumState,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spectrum/services/fp-logger-noise-spectrum/fp-logger-noise-spectrum.state';
import {
  FpLoggerNoiseSpectrumActionGetData,
  FpLoggerNoiseSpectrumActionGetDataError,
  FpLoggerNoiseSpectrumActionGetDataSuccesses,
  FpLoggerNoiseSpectrumActions,
  FpLoggerNoiseSpectrumActionToggleChartFullscreen,
  FpLoggerNoiseSpectrumActionType,
} from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/tabs/fp-logger-noise-spectrum/services/fp-logger-noise-spectrum/fp-logger-noise-spectrum.actions';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { convertChartDataInArray } from '@app/shared/utils/base-chart-data';
import {
  ILoggerNoiseSpectrumChartCommonData,
  ILoggerNoiseSpectrumChartDataPoint,
} from '@app/api/interfaces/chart-data/logger-noise-spectrum';

@Injectable()
export class FpLoggerNoiseSpectrumStore
  extends ComponentStore<IFpLoggerNoiseSpectrumState>
  implements IGcComponentStore<FpLoggerNoiseSpectrumActions>
{
  public readonly data = inject<ILdLogger>(FLOATING_POPUP_DATA);
  protected readonly store = inject(Store);
  protected readonly loggerMeasurementChartDataApiService = inject(LoggerMeasurementChartDataApiService);
  public readonly popupService: FloatingPopupService = inject(FloatingPopupService);

  constructor() {
    super(initialFpLoggerNoiseSpectrumState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly popupMainData$ = this.select(s => s.popupMainData, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly error$ = this.select(s => s.error, { equal });
  public readonly loading$ = this.select(s => s.loading, { equal });
  public readonly isChartFullscreenActive$ = this.select(s => s.isChartFullscreenActive, { equal });

  private mainProjectId = this.store.selectSignal(selectMainProjectId);

  public dispatch = this.effect((action$: Observable<FpLoggerNoiseSpectrumActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpLoggerNoiseSpectrumActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpLoggerNoiseSpectrumActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpLoggerNoiseSpectrumActionType.GetDataError: {
            this.reducerGetDataError(action);
            break;
          }
          case FpLoggerNoiseSpectrumActionType.ToggleChartFullscreen: {
            this.reducerToggleChartFullscreen();
            this.effectToggleChartFullscreen(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({ loading: true, error: null });
  }

  private reducerGetDataSuccesses(action: FpLoggerNoiseSpectrumActionGetDataSuccesses) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: action.data,
      loading: false,
    });
  }

  private reducerGetDataError(action: FpLoggerNoiseSpectrumActionGetDataError) {
    this.patchState({
      date: action.date,
      popupMainData: action.popupMainData,
      data: null,
      error: action.error,
      loading: false,
    });
  }

  private reducerToggleChartFullscreen() {
    const state = this.get();
    this.patchState({ isChartFullscreenActive: !state.isChartFullscreenActive });
  }

  private effectGetData(action: FpLoggerNoiseSpectrumActionGetData) {
    const mainProjectId = this.mainProjectId();
    const measurement = action.popupMainData?.measurement;

    if (!mainProjectId || !measurement) {
      this.dispatch({
        type: FpLoggerNoiseSpectrumActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: null,
      });
      return;
    }

    const state = this.get();

    const isOldDataValid = state.data && state.popupMainData && state.popupMainData.measurement?.id === measurement.id;

    if (isOldDataValid) {
      this.dispatch({
        type: FpLoggerNoiseSpectrumActionType.GetDataSuccesses,
        date: action.date,
        popupMainData: action.popupMainData,
        data: state.data,
      });
      return;
    }

    this.loggerMeasurementChartDataApiService
      .getSignalSpectrum(mainProjectId, measurement.id)
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpLoggerNoiseSpectrumActionType.GetDataError,
            date: action.date,
            popupMainData: action.popupMainData,
            error,
          });
          return EMPTY;
        })
      )
      .subscribe(({ data }) => {
        this.dispatch({
          type: FpLoggerNoiseSpectrumActionType.GetDataSuccesses,
          date: action.date,
          popupMainData: action.popupMainData,
          data: convertChartDataInArray<ILoggerNoiseSpectrumChartDataPoint, ILoggerNoiseSpectrumChartCommonData>(data),
        });
      });
  }

  private effectToggleChartFullscreen(action: FpLoggerNoiseSpectrumActionToggleChartFullscreen) {
    if (!action.synchronizeWithPopupId) return;

    const state = this.get();

    if (state.isChartFullscreenActive) {
      this.popupService.enterFullscreen(action.synchronizeWithPopupId, false);
    } else {
      this.popupService.exitFullscreen(action.synchronizeWithPopupId, false);
    }
  }
}
